import { Slot } from '@radix-ui/react-slot';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  type ComponentPropsWithRef,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

import { EVENTKEY_MAP } from '@repo/lib/constants';
import { useMergedRef } from '@repo/dom/hooks/useMergedRef';

import {
  useTooltip,
  useTooltipDescriptionId,
  type TooltipRelationship,
} from './TooltipRoot.js';

export type { TooltipRelationship };

// `@types/node` is deliberately absent from consuming apps' tsconfig `types`,
// so the global is untyped in their type space, and this file reaches every one
// of them through `IconButton`. Bundlers substitute the value at build time.
declare const process: { env: { NODE_ENV?: string } };

// A heuristic, not a name computation: the real thing needs
// `dom-accessibility-api`, which is not a runtime dependency of this package.
const hasAccessibleName = (trigger: HTMLElement) =>
  Boolean(trigger.getAttribute('aria-label')?.trim()) ||
  Boolean(trigger.getAttribute('aria-labelledby')) ||
  Boolean(trigger.textContent?.trim());

type DescribableProps = { 'aria-describedby'?: string };

export type TooltipTriggerProps = ComponentPropsWithRef<'span'> & {
  /**
   * Composed with the tooltip's own id, tooltip first — it adds a description,
   * it does not replace one. An `asChild` child's own value is composed the
   * same way, after this one.
   */
  'aria-describedby'?: string;
  asChild?: boolean;
  children: ReactNode;
  /**
   * What the tooltip is to this trigger.
   *
   * `description` — the default — points the trigger at the surface with
   * `aria-describedby`, so a screen reader reads the trigger's own name and
   * then the tooltip after it.
   *
   * `name` is for a trigger whose name the tooltip already repeats: an
   * icon-only control whose visually hidden label and whose tooltip are the
   * same string. It emits no `aria-describedby`, so that string is announced
   * once rather than twice.
   *
   * It supplies no name of its own — the trigger must already carry one, and a
   * development-only warning fires when it appears not to. An
   * `aria-describedby` the caller passes is still emitted; only the tooltip's
   * own id is withheld.
   */
  relationship?: TooltipRelationship;
};

export const TooltipTrigger = (props: TooltipTriggerProps) => {
  const {
    'aria-describedby': describedByProp,
    asChild,
    children,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    onPointerDown,
    ref,
    relationship = 'description',
    ...attrs
  } = props;
  const { close, open, surfacePressedRef, triggerRef } = useTooltip();
  const descriptionId = useTooltipDescriptionId(relationship);
  const mergedTriggerRef = useMergedRef<HTMLElement>(triggerRef, ref);

  // In an effect rather than during render because the check reads the mounted
  // node, which exists only once the children have rendered.
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && relationship === 'name') {
      const trigger = triggerRef.current;
      if (!trigger) return;

      // A trigger hidden from assistive tech announces nothing whether or not
      // it is named, so a missing name there is not the defect this reports.
      if (trigger.getAttribute('aria-hidden') === 'true') return;
      if (hasAccessibleName(trigger)) return;

      // eslint-disable-next-line no-console -- intentional dev warning for API misuse
      console.warn(
        'TooltipTrigger: relationship="name" withholds the tooltip id, but this trigger ' +
          'has no accessible name, so nothing is announced at all. Give the trigger a name ' +
          '(aria-label, aria-labelledby or visible text), or use relationship="description". ' +
          'This check is a heuristic and can be wrong.',
      );
    }
  }, [relationship, triggerRef]);

  const child =
    asChild && isValidElement<DescribableProps>(children) ? children : null;

  // Composed rather than overwritten, tooltip id first, as `InputControl` and
  // `InputTrigger` do. Both collisions used to sever the association outright:
  // the caller's value was left in `attrs` and spread after the tooltip's, and
  // an `asChild` child's own value won because Radix `mergeProps` composes only
  // `on*`, `style` and `className`, returning `{ ...slotProps, ...childProps }`
  // for everything else — so a child prop key shadows the `Slot`'s by merely
  // existing, whatever its value.
  const describedBy =
    [descriptionId, describedByProp, child?.props['aria-describedby']]
      .filter(Boolean)
      .join(' ') || undefined;

  const handleMouseEnter = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      onMouseEnter?.(e);
      open();
    },
    [onMouseEnter, open],
  );

  const handleMouseLeave = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      onMouseLeave?.(e);
      close();
    },
    [onMouseLeave, close],
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLSpanElement>) => {
      onFocus?.(e);
      if (e.target.matches(':focus-visible')) {
        open({ instant: true });
      }
    },
    [onFocus, open],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLSpanElement>) => {
      onBlur?.(e);
      if (surfacePressedRef.current) return;
      close();
    },
    [onBlur, close, surfacePressedRef],
  );

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLSpanElement>) => {
      onPointerDown?.(e);
      close({ instant: true });
    },
    [onPointerDown, close],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      onKeyDown?.(e);
      if (e.key === EVENTKEY_MAP.ENTER || e.key === EVENTKEY_MAP.SPACE) {
        close({ instant: true });
      }
    },
    [onKeyDown, close],
  );

  const Component = asChild ? Slot : 'span';

  // Additive to the `aria-describedby` the `Slot` already carries below, not an
  // alternative to it: the clone is what wins `mergeProps` when the child has
  // the key. Unconditional because the key's presence, not its value, is what
  // shadows — and a child without it is cloned with exactly what the `Slot`
  // would have handed it anyway.
  const trigger = child
    ? cloneElement(child, { 'aria-describedby': describedBy })
    : children;

  return (
    <Component
      aria-describedby={describedBy}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
      ref={mergedTriggerRef}
      tabIndex={asChild ? undefined : 0}
      {...attrs}
    >
      {trigger}
    </Component>
  );
};
